(function () {
  'use strict';

  const routes = window.routes || {};
  const body = document.body;
  const attributionStorageKey = 'ahanda:attribution:v13';
  const attributionTtl = 90 * 24 * 60 * 60 * 1000;
  let predictiveController;
  let predictiveTimer;

  function readStoredAttribution() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(attributionStorageKey) || 'null');
      if (!stored || !stored.updatedAt || Date.now() - stored.updatedAt > attributionTtl) return null;
      return stored;
    } catch (error) {
      return null;
    }
  }

  function inferAiSource(referrer) {
    if (!referrer) return '';
    try {
      const hostname = new URL(referrer).hostname.toLowerCase();
      if (hostname === 'chatgpt.com' || hostname.endsWith('.chatgpt.com') || hostname === 'chat.openai.com') return 'chatgpt';
      if (hostname === 'perplexity.ai' || hostname.endsWith('.perplexity.ai') || hostname === 'gemini.google.com' || hostname === 'copilot.microsoft.com') return 'other_ai';
    } catch (error) {
      return '';
    }
    return '';
  }

  function sanitiseReferrer(referrer) {
    if (!referrer) return '';
    try {
      const url = new URL(referrer);
      return `${url.origin}${url.pathname}`;
    } catch (error) {
      return '';
    }
  }

  function captureAttribution() {
    const params = new URLSearchParams(window.location.search);
    const referrer = document.referrer || '';
    const safeReferrer = sanitiseReferrer(referrer);
    const inferredSource = inferAiSource(referrer);
    let isExternalReferrer = false;
    try { isExternalReferrer = Boolean(referrer) && new URL(referrer).origin !== window.location.origin; } catch (error) { /* Ignore malformed referrers. */ }
    const source = params.get('utm_source') || inferredSource;
    const isAiSource = ['chatgpt', 'google_ai', 'other_ai'].includes(source);
    const medium = params.get('utm_medium') || (isAiSource ? 'organic_ai' : '');
    const campaign = params.get('utm_campaign') || (isAiSource ? 'geo_aeo_v13' : '');
    const content = params.get('utm_content') || '';
    const hasTouch = Boolean(source || medium || campaign || content || isExternalReferrer);
    let stored = readStoredAttribution() || { firstTouch: null, lastTouch: null, updatedAt: Date.now() };
    if (hasTouch) {
      const touch = {
        source: source || 'referral',
        medium: medium || 'referral',
        campaign,
        content,
        landingPath: window.location.pathname,
        referrer: safeReferrer
      };
      if (!stored.firstTouch) stored.firstTouch = touch;
      stored.lastTouch = touch;
      stored.updatedAt = Date.now();
      try { window.localStorage.setItem(attributionStorageKey, JSON.stringify(stored)); } catch (error) { /* Attribution is optional. */ }
    }
    return stored;
  }

  function getAttribution() {
    return captureAttribution() || { firstTouch: null, lastTouch: null };
  }

  function setAttributionFields(form) {
    const attribution = getAttribution();
    const touch = attribution.lastTouch || attribution.firstTouch || {};
    const values = {
      lead_source: touch.source || '',
      utm_source: touch.source || '',
      utm_medium: touch.medium || '',
      utm_campaign: touch.campaign || '',
      utm_content: touch.content || '',
      landing_path: touch.landingPath || window.location.pathname,
      referrer: touch.referrer || sanitiseReferrer(document.referrer || '')
    };
    Object.entries(values).forEach(([name, value]) => {
      const field = form.querySelector(`[data-attribution-field="${name}"]`);
      if (field) field.value = value;
    });
    return values;
  }

  function bindLeadAttribution() {
    captureAttribution();
    document.querySelectorAll('[data-attribution-form]').forEach((form) => {
      setAttributionFields(form);
      if (form.dataset.attributionBound) return;
      form.dataset.attributionBound = 'true';
      let started = false;
      form.addEventListener('focusin', () => {
        if (started) return;
        started = true;
        publishEvent('lead_form_start', { form_name: form.dataset.attributionForm || form.id || 'contact' });
      });
      form.addEventListener('submit', () => {
        const values = setAttributionFields(form);
        publishEvent('lead_form_submit', {
          form_name: form.dataset.attributionForm || form.id || 'contact',
          lead_source: values.lead_source,
          utm_source: values.utm_source,
          utm_medium: values.utm_medium,
          utm_campaign: values.utm_campaign,
          utm_content: values.utm_content
        });
      });
    });
  }

  function publishEvent(name, payload = {}) {
    const attribution = getAttribution();
    const touch = attribution.lastTouch || attribution.firstTouch || {};
    const detail = { ...payload, path: window.location.pathname, attribution: touch };
    document.dispatchEvent(new CustomEvent(`ahanda:${name}`, { detail }));
    if (name === 'select_item' || name === 'add_to_cart' || name === 'lead_form_start' || name === 'lead_form_submit') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: name,
        ...detail,
        ahanda_source: touch.source || '',
        ahanda_medium: touch.medium || '',
        ahanda_campaign: touch.campaign || '',
        ahanda_content: touch.content || ''
      });
    }
    try {
      if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(`ahanda_${name}`, detail);
    } catch (error) {
      // Analytics must never interrupt shopping interactions.
    }
  }

  function money(cents) {
    try {
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} TL`;
    }
  }

  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = Number(count) < 1;
    });
  }

  async function getCart() {
    const response = await fetch(routes.cart_js_url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be loaded');
    return response.json();
  }

  function getDrawer() {
    return document.querySelector('#CartDrawer');
  }

  function getSearchDrawer() {
    return document.querySelector('#SearchDrawer');
  }

  function getFilterPanel() {
    return document.querySelector('[data-filter-panel]');
  }

  function bindMegaMenus() {
    const menus = Array.from(document.querySelectorAll('[data-mega-menu]'));
    if (!menus.length) return;
    const desktop = window.matchMedia('(min-width: 901px)');
    const closeOthers = (current) => {
      menus.forEach((menu) => {
        if (menu !== current) menu.open = false;
      });
    };
    menus.forEach((menu) => {
      const summary = menu.querySelector(':scope > summary');
      if (!summary) return;
      menu.addEventListener('mouseenter', () => {
        if (!desktop.matches) return;
        closeOthers(menu);
        menu.open = true;
      });
      menu.addEventListener('mouseleave', (event) => {
        if (!desktop.matches || (event.relatedTarget && menu.contains(event.relatedTarget))) return;
        if (!menu.matches(':focus-within')) menu.open = false;
      });
      summary.addEventListener('click', () => {
        if (desktop.matches) closeOthers(menu);
      });
    });
  }

  function bindLanguageSwitcher() {
    const switchers = Array.from(document.querySelectorAll('[data-language-switcher]'));
    if (!switchers.length) return;
    switchers.forEach((switcher) => {
      const trigger = switcher.querySelector('[data-language-switcher-trigger]');
      if (!trigger) return;
      const setOpen = (open) => {
        switcher.classList.toggle('is-open', open);
        trigger.setAttribute('aria-expanded', String(open));
      };
      switcher.addEventListener('mouseenter', () => setOpen(true));
      switcher.addEventListener('mouseleave', (event) => {
        if (event.relatedTarget && switcher.contains(event.relatedTarget)) return;
        if (!switcher.matches(':focus-within')) setOpen(false);
      });
      switcher.addEventListener('focusin', () => setOpen(true));
      switcher.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!switcher.matches(':focus-within')) setOpen(false);
        }, 0);
      });
      trigger.addEventListener('click', () => setOpen(!switcher.classList.contains('is-open')));
    });
  }

  function openSearch() {
    const drawer = getSearchDrawer();
    if (!drawer) return;
    closeDrawer();
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    body.classList.add('is-locked');
    document.querySelectorAll('[data-search-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    const input = drawer.querySelector('[data-predictive-input]');
    if (input) window.setTimeout(() => input.focus(), 40);
  }

  function closeSearch() {
    const drawer = getSearchDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.querySelectorAll('[data-search-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    window.setTimeout(() => { drawer.hidden = true; }, 260);
    if (!getDrawer()?.classList.contains('is-open') && !getFilterPanel()?.classList.contains('is-open')) body.classList.remove('is-locked');
  }

  function openFilters() {
    const panel = getFilterPanel();
    const overlay = document.querySelector('[data-filter-overlay]');
    const trigger = document.querySelector('[data-filter-trigger]');
    if (!panel || !overlay) return;
    panel.classList.add('is-open');
    overlay.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    body.classList.add('is-locked');
    const closeButton = panel.querySelector('[data-filter-close]');
    if (closeButton) closeButton.focus();
  }

  function closeFilters() {
    const panel = getFilterPanel();
    const overlay = document.querySelector('[data-filter-overlay]');
    const trigger = document.querySelector('[data-filter-trigger]');
    if (!panel) return;
    panel.classList.remove('is-open');
    if (overlay) overlay.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (!getDrawer()?.classList.contains('is-open') && !getSearchDrawer()?.classList.contains('is-open')) body.classList.remove('is-locked');
  }

  function clearPredictiveResults() {
    const drawer = getSearchDrawer();
    if (!drawer) return;
    const results = drawer.querySelector('[data-predictive-results]');
    const empty = drawer.querySelector('[data-predictive-empty]');
    const all = drawer.querySelector('[data-search-all]');
    const status = drawer.querySelector('[data-predictive-status]');
    if (results) results.replaceChildren();
    if (empty) empty.hidden = true;
    if (all) all.hidden = true;
    if (status) status.textContent = '';
  }

  function appendPredictiveGroup(container, label, items, type) {
    if (!items || !items.length) return 0;
    const group = document.createElement('section');
    group.className = 'predictive-group';
    const heading = document.createElement('h3');
    heading.textContent = label;
    group.appendChild(heading);
    const list = document.createElement('div');
    list.className = 'predictive-group__list';
    items.slice(0, 6).forEach((item) => {
      const link = document.createElement('a');
      link.className = 'predictive-result';
      link.href = item.url || '#';
      const imageUrl = type === 'product' ? (item.featured_image?.url || item.image) : type === 'article' ? item.image : item.featured_image?.url;
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = item.featured_image?.alt || item.title || '';
        image.loading = 'lazy';
        link.appendChild(image);
      }
      const content = document.createElement('span');
      content.className = 'predictive-result__content';
      const title = document.createElement('strong');
      title.textContent = item.title || item.text || '';
      content.appendChild(title);
      if (type === 'product' && item.price != null) {
        const price = document.createElement('small');
        price.textContent = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Number(item.price));
        content.appendChild(price);
      }
      link.appendChild(content);
      list.appendChild(link);
    });
    group.appendChild(list);
    container.appendChild(group);
    return Math.min(items.length, 6);
  }

  function renderPredictiveResults(data, query) {
    const drawer = getSearchDrawer();
    if (!drawer) return;
    const container = drawer.querySelector('[data-predictive-results]');
    const empty = drawer.querySelector('[data-predictive-empty]');
    const all = drawer.querySelector('[data-search-all]');
    const status = drawer.querySelector('[data-predictive-status]');
    if (!container) return;
    container.replaceChildren();
    const resultGroups = data?.resources?.results || {};
    const isEnglish = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/');
    const count = appendPredictiveGroup(container, isEnglish ? 'Coffee' : 'Kahveler', resultGroups.products, 'product')
      + appendPredictiveGroup(container, isEnglish ? 'Collections' : 'Koleksiyonlar', resultGroups.collections, 'collection')
      + appendPredictiveGroup(container, isEnglish ? 'Guides' : 'Rehberler', resultGroups.articles, 'article')
      + appendPredictiveGroup(container, isEnglish ? 'Pages' : 'Sayfalar', resultGroups.pages, 'page');
    if (empty) empty.hidden = count > 0;
    if (all) {
      all.href = `${routes.search_url || '/search'}?q=${encodeURIComponent(query)}`;
      all.hidden = count === 0;
    }
    if (status) status.textContent = count ? `${count} ${isEnglish ? 'suggestions' : 'öneri'}` : '';
  }

  async function fetchPredictiveResults(query) {
    if (!routes.predictive_search_url) return;
    if (predictiveController) predictiveController.abort();
    predictiveController = new AbortController();
    const url = new URL(routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,article,page');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'all');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    const drawer = getSearchDrawer();
    const status = drawer && drawer.querySelector('[data-predictive-status]');
    const isEnglish = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/');
    if (status) status.textContent = isEnglish ? 'Searching…' : 'Aranıyor…';
    try {
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' }, signal: predictiveController.signal });
      if (!response.ok) throw new Error('Predictive search failed');
      renderPredictiveResults(await response.json(), query);
    } catch (error) {
      if (error.name !== 'AbortError' && status) status.textContent = isEnglish ? 'Search is unavailable right now.' : 'Arama şu anda kullanılamıyor.';
    }
  }

  function schedulePredictiveSearch(input) {
    const query = input.value.trim();
    clearTimeout(predictiveTimer);
    if (query.length < 2) {
      clearPredictiveResults();
      return;
    }
    predictiveTimer = window.setTimeout(() => fetchPredictiveResults(query), 180);
  }

  function openDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    body.classList.add('is-locked');
    const closeButton = drawer.querySelector('[data-cart-close]');
    if (closeButton) closeButton.focus();
  }

  function closeDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    body.classList.remove('is-locked');
    window.setTimeout(() => { drawer.hidden = true; }, 260);
  }

  async function refreshDrawer(shouldOpen) {
    const drawer = getDrawer();
    const wasOpen = drawer && drawer.classList.contains('is-open');
    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`, { headers: { Accept: 'text/html' } });
      if (response.ok) {
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const next = parsed.querySelector('#CartDrawer');
        const current = getDrawer();
        if (next && current) current.replaceWith(next);
      }
      const cart = await getCart();
      setCartCount(cart.item_count);
      if (shouldOpen || wasOpen) openDrawer();
    } catch (error) {
      const status = document.querySelector('[data-global-status]');
      if (status) status.textContent = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/') ? 'There was a problem updating your cart. Please try again.' : 'Sepeti güncellerken bir sorun oluştu. Lütfen tekrar dene.';
    }
  }

  async function changeLine(line, quantity) {
    const response = await fetch(routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!response.ok) throw new Error('Cart line could not be updated');
    await refreshDrawer(true);
  }

  async function changeCartPageLine(line, quantity) {
    const response = await fetch(routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!response.ok) throw new Error('Cart page line could not be updated');
    window.location.reload();
  }

  function showFormError(form, message) {
    const element = form.querySelector('[data-form-status]');
    if (element) {
      element.hidden = false;
      element.textContent = message;
    }
  }

  function syncGrindState(form) {
    const formOption = form.querySelector('[data-form-option-input]:checked');
    const picker = form.querySelector('[data-form-picker]');
    const value = formOption ? formOption.value : picker ? picker.value : 'Çekirdek';
    const property = form.querySelector('[data-form-property]');
    const wrapper = form.querySelector('[data-grind-method-wrapper]');
    const method = form.querySelector('[data-grind-method]');
    const isGround = /öğütülmüş|ground/i.test(value || '');
    if (property) property.value = value;
    if (wrapper) wrapper.hidden = !isGround;
    if (method) {
      method.required = isGround;
      if (!isGround) method.value = '';
    }
    return { value, isGround };
  }

  function updateVariant(form) {
    const json = form.querySelector('[data-product-json]');
    const idInput = form.querySelector('[data-variant-id]');
    if (!json || !idInput) return;
    let product;
    try { product = JSON.parse(json.textContent); } catch (error) { return; }
    const selected = Array.from(form.querySelectorAll('[data-option-position]:checked'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);
    const variant = product.variants.find((candidate) => candidate.options.every((value, index) => value === selected[index])) || product.variants.find((candidate) => candidate.available) || product.variants[0];
    if (!variant) return;
    idInput.value = variant.id;
    const price = form.querySelector('[data-product-price]') || form.closest('[data-product-layout]')?.querySelector('[data-product-price]');
    const compare = form.querySelector('[data-compare-price]') || form.closest('[data-product-layout]')?.querySelector('[data-compare-price]');
    if (price) price.textContent = money(variant.price);
    if (compare) {
      compare.textContent = variant.compare_at_price > variant.price ? money(variant.compare_at_price) : '';
      compare.hidden = !(variant.compare_at_price > variant.price);
    }
    const submit = form.querySelector('[data-add-to-cart]');
    if (submit) {
      const submitLabel = submit.querySelector('[data-add-to-cart-label-text]');
      const isEnglish = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/');
      const availableLabel = submit.dataset.addToCartLabel || (isEnglish ? 'Add to cart' : 'Sepete ekle');
      submit.disabled = !variant.available;
      if (submitLabel) submitLabel.textContent = variant.available ? availableLabel : (isEnglish ? 'Sold out' : 'Tükendi');
      else submit.textContent = variant.available ? availableLabel : (isEnglish ? 'Sold out' : 'Tükendi');
    }
    syncGrindState(form);
  }

  function bindProductForms() {
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      updateVariant(form);
      form.querySelectorAll('[data-option-position]').forEach((input) => {
        input.addEventListener('change', () => updateVariant(form));
      });
      const picker = form.querySelector('[data-form-picker]');
      if (picker) picker.addEventListener('change', () => syncGrindState(form));
      form.querySelectorAll('[data-form-choice]').forEach((input) => {
        input.addEventListener('change', () => {
          if (picker) picker.value = input.value;
          syncGrindState(form);
        });
      });
    });
  }

  function bindProductGalleries() {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-gallery-main]');
      const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      if (!main || !thumbs.length) return;
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const image = thumb.dataset.galleryImage;
          if (!image) return;
          main.removeAttribute('srcset');
          main.removeAttribute('sizes');
          main.src = image;
          main.alt = thumb.dataset.galleryAlt || main.alt;
          thumbs.forEach((item) => item.classList.toggle('is-active', item === thumb));
        });
      });
    });
  }

  function bindTasteQuiz() {
    const quiz = document.querySelector('[data-taste-quiz]');
    if (!quiz) return;
    const intro = quiz.querySelector('[data-quiz-intro]');
    const flow = quiz.querySelector('[data-quiz-flow]');
    const result = quiz.querySelector('[data-quiz-result]');
    const questions = Array.from(quiz.querySelectorAll('[data-quiz-question]'));
    const answers = {};
    let step = 0;
    const roastHandles = { light: 'ethiopia-filtre-kahve', medium: 'kolombiya-filtre-kahve', dark: 'espresso-kahve' };

    function renderStep() {
      questions.forEach((question, index) => { question.hidden = index !== step; });
      const progress = quiz.querySelector('[data-quiz-progress]');
      const bar = quiz.querySelector('[data-quiz-progress-bar]');
      const back = quiz.querySelector('[data-quiz-back]');
      const next = quiz.querySelector('[data-quiz-next]');
      const nextLabel = quiz.querySelector('[data-quiz-next-label]');
      const stepLabel = quiz.querySelector('[data-quiz-step-label]');
      const progressBar = quiz.querySelector('.quiz-progress');
      const activeAnswer = answers[step];
      if (progress) progress.textContent = `${step + 1} / ${questions.length}`;
      if (bar) bar.style.width = `${((step + 1) / questions.length) * 100}%`;
      if (back) back.hidden = step === 0;
      if (stepLabel) stepLabel.textContent = questions[step]?.dataset.quizLabel || `Soru ${step + 1}`;
      if (next) next.disabled = !activeAnswer;
      if (nextLabel) nextLabel.textContent = step === questions.length - 1 ? 'Sonucu gör' : 'Devam Et';
      if (progressBar) progressBar.setAttribute('aria-valuenow', String(step + 1));
      questions[step]?.querySelectorAll('[data-quiz-answer]').forEach((answer) => {
        answer.classList.toggle('is-selected', answer.dataset.quizAnswer === activeAnswer);
      });
    }

    function choose(value) {
      answers[step] = value;
      const feedback = quiz.querySelector('[data-quiz-feedback]');
      if (feedback) feedback.hidden = true;
      publishEvent('quiz_answer', { step: step + 1, answer: value });
      renderStep();
    }

    function showResult() {
      const fallback = quiz.querySelector('[data-quiz-result-fallback]');
      let unique = [];
      try {
        const brew = answers[0];
        const taste = answers[1];
        const roast = answers[2];
        const routine = answers[3];
        const brewHandles = { machine: 'kolombiya-filtre-kahve', v60: 'ethiopia-filtre-kahve', press: 'brezilya-filtre-kahve', espresso: 'espresso-kahve', turk: 'turk-kahvesi-geleneksel', unsure: 'kolombiya-filtre-kahve' };
        const tasteHandles = { floral: 'ethiopia-filtre-kahve', fruit: 'kenya-filtre-kahve', sweet: 'brezilya-filtre-kahve', bold: 'espresso-kahve' };
        const routineHandle = routine === 'office'
          ? 'ofiste-kahve-paketi'
          : routine === 'many'
            ? (brew === 'turk' ? 'mudavim-turk-kahvesi-paketi' : 'ozel-harman-filtre-kahve')
            : '';
        const primary = routineHandle || (brew === 'turk'
          ? brewHandles.turk
          : brew === 'espresso'
            ? brewHandles.espresso
            : tasteHandles[taste] || brewHandles[brew] || roastHandles[roast] || 'kolombiya-filtre-kahve');
        const alternatives = [primary, tasteHandles[taste], brewHandles[brew], roastHandles[roast]];
        const title = quiz.querySelector('[data-quiz-result-title]');
        const text = quiz.querySelector('[data-quiz-result-text]');
        const cards = quiz.querySelector('[data-quiz-result-products]');
        unique = alternatives.filter(Boolean).filter((handle, index, list) => list.indexOf(handle) === index).slice(0, 3);
        if (fallback) fallback.hidden = true;
        if (title) title.textContent = routine === 'office' ? 'Ekibin için iyi bir başlangıç.' : routine === 'many' ? 'Daha çok fincan için seçtiklerimiz.' : 'İşte senin için seçtiğimiz kahve!';
        if (text) text.textContent = answers[4] === 'ground' ? 'Öğütülmüş seçiminde ürün kartından demleme yöntemini de belirtebilirsin. Böylece fincanına daha hazır bir başlangıç yaparsın.' : 'Yanıtlarına göre damak tadına ve demleme ritmine en uygun seçenekleri bir araya getirdik.';
        let visibleCount = 0;
        if (cards) {
          const availableCards = Array.from(cards.querySelectorAll('[data-quiz-result-card]'));
          availableCards.forEach((card) => {
            const match = unique.indexOf(card.dataset.quizResultCard);
            const isVisible = match > -1;
            card.hidden = !isVisible;
            if (isVisible) visibleCount += 1;
            card.classList.toggle('quiz-result-card--primary', match === 0);
            const matchLabel = card.querySelector('[data-quiz-match-label]');
            if (matchLabel && isVisible) matchLabel.textContent = match === 0 ? 'En iyi eşleşme' : 'Alternatif eşleşme';
          });
        }
        if (!cards || visibleCount === 0) {
          if (fallback) fallback.hidden = false;
        }
        publishEvent('quiz_complete', { answers: { ...answers }, recommendations: unique });
      } catch (error) {
        if (fallback) fallback.hidden = false;
        publishEvent('quiz_error', { message: error?.message || 'Quiz sonucu hazırlanamadı' });
      }
      if (flow) flow.hidden = true;
      if (intro) intro.hidden = true;
      if (result) result.hidden = false;
      result?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function reset() {
      step = 0;
      Object.keys(answers).forEach((key) => delete answers[key]);
      quiz.querySelectorAll('.is-selected').forEach((element) => element.classList.remove('is-selected'));
      quiz.querySelectorAll('[data-quiz-result-card]').forEach((card) => {
        card.hidden = true;
        card.classList.remove('quiz-result-card--primary');
      });
      const feedback = quiz.querySelector('[data-quiz-feedback]');
      if (feedback) feedback.hidden = true;
      if (result) result.hidden = true;
      if (intro) intro.hidden = false;
      if (flow) flow.hidden = true;
      const fallback = quiz.querySelector('[data-quiz-result-fallback]');
      if (fallback) fallback.hidden = true;
      publishEvent('quiz_reset');
      renderStep();
      intro?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    quiz.addEventListener('click', (event) => {
      const start = event.target.closest('[data-quiz-start]');
      if (start) { publishEvent('quiz_start'); intro.hidden = true; flow.hidden = false; renderStep(); flow.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
      const answer = event.target.closest('[data-quiz-answer]');
      if (answer) { choose(answer.dataset.quizAnswer); return; }
      if (event.target.closest('[data-quiz-next]')) {
        if (!answers[step]) {
          const feedback = quiz.querySelector('[data-quiz-feedback]');
          if (feedback) feedback.hidden = false;
          return;
        }
        if (step < questions.length - 1) { step += 1; renderStep(); return; }
        showResult();
        return;
      }
      if (event.target.closest('[data-quiz-back]')) { step = Math.max(0, step - 1); renderStep(); return; }
      if (event.target.closest('[data-quiz-reset]')) reset();
    });
    renderStep();
  }

  function bindGuideFilters() {
    document.querySelectorAll('[data-guide-filters]').forEach((filters) => {
      const cards = Array.from(document.querySelectorAll('[data-guide-card]'));
      filters.addEventListener('click', (event) => {
        const button = event.target.closest('[data-guide-filter]');
        if (!button) return;
        const filter = button.dataset.guideFilter;
        filters.querySelectorAll('[data-guide-filter]').forEach((item) => item.classList.toggle('is-active', item === button));
        cards.forEach((card) => { card.hidden = filter !== 'all' && card.dataset.guideCard !== filter; });
        publishEvent('guide_filter', { filter });
      });
    });
  }

  function bindGuidePreview() {
    const panels = document.querySelector('[data-guide-preview-panels]');
    const landing = document.querySelector('[data-guide-landing-shell]');
    if (!panels || !landing) return;
    const query = new URLSearchParams(window.location.search).get('guide') || window.location.hash.replace('#guide=', '');
    if (!query || !panels.querySelector(`[data-guide-preview="${CSS.escape(query)}"]`)) return;
    landing.hidden = true;
    panels.hidden = false;
    panels.querySelectorAll('[data-guide-preview]').forEach((panel) => { panel.hidden = panel.dataset.guidePreview !== query; });
  }

  function bindFaqSearch() {
    document.querySelectorAll('[data-faq-page]').forEach((page) => {
      const input = page.querySelector('[data-faq-search]');
      const status = page.querySelector('[data-faq-search-status]');
      const empty = page.querySelector('[data-faq-empty]');
      const items = Array.from(page.querySelectorAll('[data-faq-item]'));
      const categories = Array.from(page.querySelectorAll('[data-faq-category]'));
      const navLinks = Array.from(page.querySelectorAll('[data-faq-nav-link]'));
      if (!input) return;

      function filterFaq() {
        const query = input.value.trim().toLocaleLowerCase('tr-TR');
        let matches = 0;
        items.forEach((item) => {
          const searchableText = item.textContent.toLocaleLowerCase('tr-TR');
          const isMatch = !query || searchableText.includes(query);
          item.hidden = !isMatch;
          if (isMatch) matches += 1;
        });
        categories.forEach((category) => {
          const hasMatch = Boolean(category.querySelector('[data-faq-item]:not([hidden])'));
          category.hidden = Boolean(query && !hasMatch);
          const link = navLinks.find((item) => item.dataset.faqNavLink === category.dataset.faqCategory);
          if (link) link.hidden = category.hidden;
        });
        if (status) {
          status.hidden = !query;
          status.textContent = query ? `${matches} soru bulundu.` : '';
        }
        if (empty) empty.hidden = !query || matches > 0;
      }

      input.addEventListener('input', filterFaq);
    });
  }

  function bindOrderTracking() {
    document.querySelectorAll('[data-order-tracking]').forEach((page) => {
      const form = page.querySelector('[data-order-tracking-form]');
      const result = page.querySelector('[data-order-tracking-result]');
      const error = page.querySelector('[data-order-tracking-error]');
      const orderOutput = page.querySelector('[data-order-tracking-order]');
      const supportLink = page.querySelector('[data-order-tracking-support-link]');
      const reset = page.querySelector('[data-order-tracking-reset]');
      if (!form || !result) return;

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const emailInput = form.querySelector('[name="email"]');
        const orderInput = form.querySelector('[name="order_number"]');
        const email = emailInput?.value.trim() || '';
        const orderNumber = orderInput?.value.trim() || '';
        if (!email || !emailInput.checkValidity() || !orderNumber) {
          if (error) error.hidden = false;
          if (!email || !emailInput.checkValidity()) emailInput?.focus();
          else orderInput?.focus();
          return;
        }
        if (error) error.hidden = true;
        if (orderOutput) orderOutput.textContent = orderNumber;
        if (supportLink) {
          const subject = encodeURIComponent(`Sipariş takibi ${orderNumber}`);
          const body = encodeURIComponent(`Sipariş numarası: ${orderNumber}\nSipariş e-postası: ${email}`);
          supportLink.href = `mailto:${page.dataset.supportEmail || ''}?subject=${subject}&body=${body}`;
        }
        form.hidden = true;
        result.hidden = false;
        result.focus();
      });

      reset?.addEventListener('click', () => {
        form.reset();
        form.hidden = false;
        result.hidden = true;
        if (error) error.hidden = true;
        form.querySelector('[name="email"]')?.focus();
      });
    });
  }

  function bindTrackingWhatsApp() {
    document.querySelectorAll('[data-tracking-whatsapp]').forEach((banner) => {
      const input = banner.querySelector('[data-tracking-whatsapp-order]');
      const link = banner.querySelector('[data-tracking-whatsapp-link]');
      const hint = banner.querySelector('[data-tracking-whatsapp-hint]');
      const number = banner.dataset.whatsappNumber || '';
      if (!input || !link || !number) return;

      const updateLink = () => {
        const rawOrderNumber = input.value.trim().replace(/^#+/, '');
        if (!rawOrderNumber) {
          link.href = `https://wa.me/${number}`;
          return false;
        }
        const message = `Merhaba, #${rawOrderNumber} No'lu siparişim hakkında bilgi almak istiyorum.`;
        link.href = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
        return true;
      };

      input.addEventListener('input', () => {
        updateLink();
        if (hint) hint.hidden = true;
      });

      link.addEventListener('click', (event) => {
        if (!updateLink()) {
          event.preventDefault();
          if (hint) hint.hidden = false;
          input.focus();
        }
      });
    });
  }

  function bindCustomerAuth() {
    document.querySelectorAll('[data-customer-auth]').forEach((page) => {
      const recover = page.querySelector('[data-auth-recover]');
      const loginForm = page.querySelector('#CustomerLoginForm');
      const loginHeading = page.querySelector('[data-auth-login-heading]')?.closest('.customer-auth__heading');
      const loginDescription = page.querySelector('[data-auth-login-description]');
      const divider = page.querySelector('.customer-auth__divider');
      const trust = page.querySelector('.customer-auth__trust');
      const switchLink = page.querySelector('.customer-auth__switch');
      if (!recover || !loginForm) return;

      function showRecover(shouldUpdateHash) {
        recover.hidden = false;
        loginForm.hidden = true;
        if (loginHeading) loginHeading.hidden = true;
        if (divider) divider.hidden = true;
        if (trust) trust.hidden = true;
        if (switchLink) switchLink.hidden = true;
        if (shouldUpdateHash) history.replaceState(null, '', '#recover');
        recover.querySelector('input')?.focus();
      }

      function showLogin(shouldUpdateHash) {
        recover.hidden = true;
        loginForm.hidden = false;
        if (loginHeading) loginHeading.hidden = false;
        if (divider) divider.hidden = false;
        if (trust) trust.hidden = false;
        if (switchLink) switchLink.hidden = false;
        if (shouldUpdateHash) history.replaceState(null, '', window.location.pathname);
        loginForm.querySelector('input')?.focus();
      }

      page.addEventListener('click', (event) => {
        if (event.target.closest('[data-auth-recover-trigger]')) { event.preventDefault(); showRecover(true); }
        if (event.target.closest('[data-auth-login-trigger]')) { event.preventDefault(); showLogin(true); }
      });
      if (window.location.hash === '#recover') showRecover(false);
      else if (loginDescription) loginDescription.hidden = false;
    });
  }

  function bindCoffeeCalculator() {
    document.querySelectorAll('[data-coffee-calculator]').forEach((calculator) => {
      const cupsInput = calculator.querySelector('[data-coffee-cups]');
      const cupsOutput = calculator.querySelector('[data-coffee-cups-output]');
      const monthlyOutput = calculator.querySelector('[data-coffee-monthly]');
      const weeklyOutput = calculator.querySelector('[data-coffee-weekly]');
      const dailyOutput = calculator.querySelector('[data-coffee-daily]');
      if (!cupsInput || !cupsOutput || !monthlyOutput || !weeklyOutput || !dailyOutput) return;

      const formatKg = (grams) => `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(grams / 1000)} kg`;
      const formatGrams = (grams) => `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(grams)} g`;
      const update = () => {
        const cups = Math.max(1, Number(cupsInput.value || 1));
        const dailyGrams = cups * 18;
        const weeklyGrams = dailyGrams * 7;
        const monthlyGrams = dailyGrams * 30;
        cupsOutput.textContent = `${cups} fincan`;
        cupsInput.setAttribute('aria-valuetext', `${cups} fincan`);
        dailyOutput.textContent = formatGrams(dailyGrams);
        weeklyOutput.textContent = formatKg(weeklyGrams);
        monthlyOutput.textContent = formatKg(monthlyGrams);
      };

      cupsInput.addEventListener('input', update);
      update();
    });
  }

  async function addProduct(form) {
    const grind = syncGrindState(form);
    const formData = new FormData(form);
    if (grind.isGround && !formData.get('properties[Demleme yöntemi]')) {
      showFormError(form, window.theme?.language === 'en' || window.location.pathname.startsWith('/en/') ? 'Select a brewing method for ground coffee.' : 'Öğütülmüş kahve için demleme yöntemini seçmelisin.');
      const method = form.querySelector('[data-grind-method]');
      if (method) method.focus();
      return;
    }
    const submit = form.querySelector('[data-add-to-cart]');
    const submitLabel = submit?.querySelector('[data-add-to-cart-label-text]');
    const originalText = submit ? (submitLabel ? submitLabel.textContent : submit.textContent) : '';
    const crossSellCard = form.closest('[data-cross-sell-card]');
    if (submit) {
      submit.disabled = true;
      const isEnglish = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/');
      if (submitLabel) submitLabel.textContent = isEnglish ? 'Adding…' : 'Ekleniyor…';
      else submit.textContent = isEnglish ? 'Adding…' : 'Ekleniyor…';
    }
    try {
      const response = await fetch(routes.cart_add_url, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      if (!response.ok) throw new Error('Product could not be added');
      publishEvent('add_to_cart', {
        variantId: form.querySelector('[data-variant-id]')?.value,
        quantity: formData.get('quantity') || 1,
        item_list_name: form.dataset.itemListName || undefined,
        item_id: form.dataset.productId || crossSellCard?.dataset.productId || undefined,
        item_name: form.dataset.productTitle || crossSellCard?.dataset.productTitle || undefined
      });
      await refreshDrawer(true);
    } catch (error) {
      showFormError(form, window.theme?.language === 'en' || window.location.pathname.startsWith('/en/') ? 'We could not add this product to the cart. Please try another option.' : 'Bu ürünü şu anda sepete ekleyemiyoruz. Lütfen başka bir seçenek dene.');
    } finally {
      if (submit) {
        submit.disabled = false;
        const isEnglish = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/');
        if (submitLabel) submitLabel.textContent = originalText || submit.dataset.addToCartLabel || (isEnglish ? 'Add to cart' : 'Sepete ekle');
        else submit.textContent = originalText || (isEnglish ? 'Add to cart' : 'Sepete ekle');
      }
    }
  }

  document.addEventListener('click', (event) => {
    const crossSellLink = event.target.closest('[data-cross-sell-link]');
    if (crossSellLink) {
      publishEvent('select_item', {
        list_name: crossSellLink.dataset.listName || 'cross_sell_pdp',
        item_id: crossSellLink.dataset.productId || undefined,
        item_name: crossSellLink.dataset.productTitle || undefined
      });
    }
    const funnelLink = event.target.closest('[data-funnel-link]');
    if (funnelLink) publishEvent('funnel_click', { name: funnelLink.dataset.funnelLink, label: funnelLink.dataset.funnelLabel || '', href: funnelLink.href || '' });
    const cartTrigger = event.target.closest('[data-cart-trigger]');
    if (cartTrigger) { event.preventDefault(); openDrawer(); return; }
    if (event.target.closest('[data-cart-close]') || event.target.closest('[data-cart-overlay]')) { closeDrawer(); return; }
    const searchTrigger = event.target.closest('[data-search-trigger]');
    if (searchTrigger) { event.preventDefault(); openSearch(); return; }
    if (event.target.closest('[data-search-close]') || event.target.closest('[data-search-overlay]')) { closeSearch(); return; }
    if (event.target.closest('[data-filter-trigger]')) { openFilters(); return; }
    if (event.target.closest('[data-filter-close]') || event.target.closest('[data-filter-overlay]')) { closeFilters(); return; }
    const mobileTrigger = event.target.closest('[data-mobile-menu-trigger]');
    if (mobileTrigger) {
      const menu = document.querySelector('[data-mobile-menu]');
      if (!menu) return;
      const isOpen = !menu.hidden;
      menu.hidden = isOpen;
      mobileTrigger.setAttribute('aria-expanded', String(!isOpen));
      return;
    }
    const productQuantity = event.target.closest('[data-product-quantity]');
    if (productQuantity) {
      const form = productQuantity.closest('[data-product-form]');
      const input = form && form.querySelector('[data-product-quantity-input]');
      const hidden = form && form.querySelector('[data-quantity-value]');
      if (input) {
        const current = Number(input.value || 1);
        const next = productQuantity.dataset.productQuantity === 'increase' ? current + 1 : current - 1;
        input.value = Math.max(1, next);
        if (hidden) hidden.value = input.value;
      }
      return;
    }
    const quantity = event.target.closest('[data-cart-quantity]');
    if (quantity) {
      const line = Number(quantity.dataset.line);
      const current = Number(quantity.dataset.current || 1);
      const next = quantity.dataset.direction === 'increase' ? current + 1 : current - 1;
      const cartPage = quantity.closest('[data-cart-page]');
      if (cartPage) changeCartPageLine(line, Math.max(0, next)).catch(() => {});
      else changeLine(line, Math.max(0, next)).catch(() => {});
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      const cartPage = remove.closest('[data-cart-page]');
      if (cartPage) changeCartPageLine(Number(remove.dataset.line), 0).catch(() => {});
      else changeLine(Number(remove.dataset.line), 0).catch(() => {});
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeDrawer();
      closeSearch();
      closeFilters();
      document.querySelectorAll('[data-mega-menu][open]').forEach((menu) => { menu.open = false; });
      document.querySelectorAll('[data-language-switcher].is-open').forEach((switcher) => {
        switcher.classList.remove('is-open');
        const trigger = switcher.querySelector('[data-language-switcher-trigger]');
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          trigger.focus();
        }
      });
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-predictive-input]')) schedulePredictiveSearch(event.target);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    addProduct(form);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-collection-sort]')) {
      publishEvent('collection_sort', { value: event.target.value });
      return;
    }
    if (!event.target.matches('[data-product-quantity-input]')) return;
    const form = event.target.closest('[data-product-form]');
    const hidden = form && form.querySelector('[data-quantity-value]');
    const value = Math.max(1, Number(event.target.value || 1));
    event.target.value = value;
    if (hidden) hidden.value = value;
  });

  bindProductForms();
  bindProductGalleries();
  bindMegaMenus();
  bindLanguageSwitcher();
  bindGuideFilters();
  bindGuidePreview();
  bindFaqSearch();
  bindOrderTracking();
  bindTrackingWhatsApp();
  bindCustomerAuth();
  bindCoffeeCalculator();
  bindTasteQuiz();
  bindLeadAttribution();
})();
